// Final Outcome panel.
//
// Per-lane Pass/Fail/Awaiting/N-A + Combined DQR + Overall outcome.
// Pending labels stay italic until every non-N/A lane has issued.

import { AlertCircle, CheckCircle2, Minus, XOctagon } from "lucide-react";
import { Badge, Card } from "@/design-system/components/ui";
import type {
  FinalOutcomePayload,
  LaneOutcomeRow,
  LaneOutcomeState,
} from "@/api/dealFlow/types";

interface Props {
  outcome: FinalOutcomePayload;
}

const LANE_STATE_META: Record<
  LaneOutcomeState,
  { label: string; icon: typeof CheckCircle2; tone: string }
> = {
  Pass: { label: "Pass", icon: CheckCircle2, tone: "text-status-success" },
  Fail: { label: "Fail", icon: XOctagon, tone: "text-status-error" },
  Awaiting: { label: "Awaiting", icon: AlertCircle, tone: "text-gray-mid" },
  NotApplicable: { label: "N/A", icon: Minus, tone: "text-gray-mid" },
};

export function FinalOutcomePanel({ outcome }: Props) {
  return (
    <Card className="p-space-4" aria-labelledby="final-outcome-heading">
      <h3
        id="final-outcome-heading"
        className="font-display text-h4 text-onyx uppercase tracking-wider mb-space-3"
      >
        Final Outcome
      </h3>

      <div className="text-label uppercase tracking-label text-gray-mid mb-space-1">
        Lane outcomes
      </div>
      <ul className="flex flex-col gap-space-1 mb-space-3">
        {outcome.laneOutcomes.map((row) => (
          <LaneRow key={row.laneCode} row={row} />
        ))}
      </ul>

      <dl className="grid grid-cols-2 gap-space-3 text-body">
        <div>
          <dt className="text-label uppercase tracking-label text-gray-mid">Combined DQR</dt>
          <dd className={outcome.combinedDealQualityRating === null ? "italic text-gray-mid" : "text-onyx font-semibold"}>
            {outcome.combinedDealQualityRating === null
              ? "Pending"
              : `${outcome.combinedDealQualityRating} (${outcome.combinedDealQualityRatingLabel})`}
          </dd>
        </div>
        <div>
          <dt className="text-label uppercase tracking-label text-gray-mid">Overall outcome</dt>
          <dd>
            {outcome.overallOutcome === "Pending" ? (
              <span className="italic text-gray-mid">Pending</span>
            ) : outcome.overallOutcome === "Pass" ? (
              <Badge status="finalized">
                <CheckCircle2 size={12} aria-hidden />
                <span className="ml-space-1">Pass</span>
              </Badge>
            ) : (
              <Badge status="error">
                <XOctagon size={12} aria-hidden />
                <span className="ml-space-1">Fail</span>
              </Badge>
            )}
          </dd>
        </div>
      </dl>

      <p className="text-small text-gray-mid mt-space-3" role="note">
        {outcome.note}
      </p>
    </Card>
  );
}

function LaneRow({ row }: { row: LaneOutcomeRow }) {
  const meta = LANE_STATE_META[row.state];
  const Icon = meta.icon;
  return (
    <li className="grid grid-cols-12 items-center gap-space-2">
      <span className="col-span-2 font-mono text-onyx">{row.laneCode}</span>
      <span className="col-span-5 text-onyx">{row.laneDisplayName}</span>
      <span
        className={`col-span-3 inline-flex items-center gap-space-1 ${meta.tone}`}
        aria-label={`${row.laneDisplayName}: ${meta.label}`}
      >
        <Icon size={14} aria-hidden />
        <span className="font-semibold">{meta.label}</span>
      </span>
      <span className="col-span-2 text-small text-gray-mid text-right">
        {row.dealQualityRating === null
          ? "—"
          : `DQR: ${row.dealQualityRating} (${row.dealQualityRatingLabel})`}
      </span>
    </li>
  );
}
